import { Component, computed, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { OnlineIndicatorComponent } from "../../../../shared/components/online-indicator/online-indicator.component";
import { firstValueFrom } from "rxjs";
import { InspectionJob, Lot } from "../../../../core/database/db";
import { InspectionJobDetail } from "../../../../domains/jobs/models/InspectionJobDetail";
import { JobSkeletonComponent } from "../../../ccm/BQ7/job-schedule/job-skeleton/job-skeleton.component";
import { BackgroundRefreshIndicatorComponent } from "../../../../shared/components/background-refresh-indicator/background-refresh-indicator.component";
import { ImageHydrationService } from "../../../../core/sync/image-sync/image-hydration.service";
import { DateUtilsService } from "../../../../shared/services/date.service";
import { LoggerService } from "../../../../shared/services/logger.service";
import { SupportFooterComponent } from "../../../../shared/components/support-footer/support-footer.component";
import { ConstructionManagerScheduleService } from "../../../../domains/jobs/services/construction-manager-schedule.service";
import { BqaJobSyncMetadata, DataSyncMetadataService, SyncMetadata } from "../../../../core/sync/data-sync/data-sync-metadata.service";
import { TabsModule } from "primeng/tabs";
import {
  canUseIssueHistoryForStartedState,
  createScheduleJobDetail,
  ScheduleJobDetail
} from "./models/ScheduleJobDetail";
import { buildScheduleLotCards, ScheduleLotCard, ScheduleLotCardContext } from "./models/ScheduleLotCard";
import { ScheduleSection, ScheduleSectionKey } from "./models/ScheduleSection";
import { formatDateRangeLabel, formatMonthDay } from "../../../../shared/utils/formatting-helper";
import { ScheduleLotCardComponent } from "./components/schedule-lot-card/schedule-lot-card.component";
import { FeatureFlag, FeatureService } from "../../../../core/feature-flags/feature.service";
import { JobStatus } from "../../../ccm/BQ7/job-schedule/job-status";
import { BottomSheetComponent } from "../../../../shared/components/bottom-sheet/bottom-sheet.component";
import { SyncStatusSheetComponent } from "../../../ccm/BQ7/job-schedule/job-sync-status-drawer/job-sync-status-drawer.component";
import { NetworkStatusService } from "../../../../shared/services/network-status.service";
import SyncStatusService, { SyncStatus } from "../../../../core/sync/sync-status-service";
import { UiToastService } from "../../../../shared/components/toast/ui-toast.service";
import { ChecklistService } from "../../../../shared/services/checklist.service";
import { CONSTANTS } from "../../../../constants";
import { JobIssueService } from "../../../../domains/jobIssues/services/job-issue-service";
import { LotService } from "../../../../domains/lots/services/lot.service";
import { DataUpdatedFadeDirective } from "../../../../shared/directives/data-updated-fade.directive";
import { ChipModule } from "primeng/chip";
import { AdminConfigKey, LotPrimaryIdentifier } from "../../../../core/feature-flags/models/AdminConfigDto";
import { ScheduleFilterChip, ScheduleFilterService } from "./services/schedule-filter.service";
import { ScheduleFilterPanelComponent } from "./components/schedule-filter-panel/schedule-filter-panel.component";

type AnyJob = InspectionJob | ScheduleJobDetail;

export enum ScheduleTab {
  ThisWeek = "ThisWeek",
  LastWeek = "LastWeek"
}

/** Steps scheduled this close (in business days) can be worked without starting them early. */
const READY_WINDOW_BUSINESS_DAYS = 2;

interface ScheduleWeekBoundaries {
  today: Date;
  startOfWeek: Date;
  endOfWeek: Date;
  /** Tomorrow, where the "This Week" section picks up. */
  startOfRemainingWeek: Date;
  startOfNextWeek: Date;
  endOfNextWeek: Date;
}

@Component({
  selector: "app-job-schedule-cm",
  standalone: true,
  imports: [
    CommonModule,
    OnlineIndicatorComponent,
    JobSkeletonComponent,
    BackgroundRefreshIndicatorComponent,
    SupportFooterComponent,
    TabsModule,
    ScheduleLotCardComponent,
    BottomSheetComponent,
    SyncStatusSheetComponent,
    DataUpdatedFadeDirective,
    ChipModule,
    ScheduleFilterPanelComponent
  ],
  templateUrl: "./job-schedule.component.html",
  styleUrls: ["./job-schedule.component.scss"]
})
export class JobScheduleComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly scheduleFilterService = inject(ScheduleFilterService);

  constructor(
    protected constructionManagerScheduleService: ConstructionManagerScheduleService,
    private router: Router,
    private mediaHydration: ImageHydrationService,
    private dateUtils: DateUtilsService,
    private logger: LoggerService,
    private dataSyncMetadataService: DataSyncMetadataService,
    private featureService: FeatureService,
    private networkService: NetworkStatusService,
    private syncStatusService: SyncStatusService,
    private toast: UiToastService,
    private jobIssueService: JobIssueService,
    private lotService: LotService,
    private checklistService: ChecklistService
  ) {
    this.networkService.online$.pipe(takeUntilDestroyed()).subscribe(val => {
      this.isOnline = val;
    });
  }

  CONSTANTS = CONSTANTS;
  ScheduleTabEnum = ScheduleTab;

  isOnline: boolean = true;
  syncMetadata: Record<string, BqaJobSyncMetadata> = {};
  jobStatusEnum = JobStatus;
  isBottomSheetOpen = false;
  selectedJob: InspectionJob | null = null;
  title: string = "My Scheduled BQA Reviews";
  today!: Date;
  dataLoaded: boolean = false;
  isRefreshingInBackground = signal(false);
  dataUpdatedPulse = signal(false);
  activeTab = signal<ScheduleTab>(ScheduleTab.ThisWeek);
  isFilterSheetOpen = false;
  lotPrimaryIdentifier = signal<LotPrimaryIdentifier>(LotPrimaryIdentifier.LotBlock);

  /** One card per lot, already reduced to the step the CM should look at next. */
  lotCards = signal<ScheduleLotCard[]>([]);

  readonly appliedFilterCount = this.scheduleFilterService.selectedCount;

  // The sort always has a value, so it shows permanently and has nothing to remove back to.
  readonly appliedFilterChips = computed(() => [
    { ...this.scheduleFilterService.getSortChip(this.lotPrimaryIdentifier()), removable: false },
    ...this.scheduleFilterService.activeChips().map(chip => ({ ...chip, removable: true }))
  ]);

  /** What the tabs actually render: the applied filters, in the chosen order. */
  readonly visibleCards = computed(() => this.scheduleFilterService.sortCards(
    this.scheduleFilterService.applyFilters(this.lotCards()),
    this.lotPrimaryIdentifier(),
    this.scheduleFilterService.sortDirection()
  ));

  /**
   * Unfiltered cards the two tabs can reach. Options are drawn from these rather than every cached
   * card, so the CM is never offered a value that would come back empty.
   */
  readonly filterableCards = computed(() => {
    const boundaries = this.getWeekBoundaries();
    const from = this.dateUtils.addDays(boundaries.startOfWeek, -7).getTime();
    const to = boundaries.endOfNextWeek.getTime();

    return this.lotCards().filter(card => {
      const cardDate = this.dateUtils.normalizeToLocalDate(card.effectiveDate).getTime();

      return cardDate >= from && cardDate <= to;
    });
  });

  /** Earlier in the current week, so past due; pinned above the dated sections with no header. */
  overdueCards = computed(() => {
    const boundaries = this.getWeekBoundaries();

    return this.cardsInRange(boundaries.startOfWeek, this.dateUtils.addDays(boundaries.today, -1));
  });

  scheduleSections = computed<ScheduleSection[]>(() => {
    const boundaries = this.getWeekBoundaries();
    const sections: ScheduleSection[] = [{
      key: ScheduleSectionKey.Today,
      label: "Today",
      dateLabel: formatMonthDay(boundaries.today),
      showCardDates: false,
      cards: this.cardsInRange(boundaries.today, boundaries.today)
    }];

    // Viewed on a Sunday there is no day left between tomorrow and the end of the week, so the
    // section has no range to label and is dropped rather than rendered permanently empty.
    if (boundaries.startOfRemainingWeek <= boundaries.endOfWeek) {
      sections.push({
        key: ScheduleSectionKey.ThisWeek,
        label: "This Week",
        dateLabel: formatDateRangeLabel(boundaries.startOfRemainingWeek, boundaries.endOfWeek),
        showCardDates: boundaries.startOfRemainingWeek.getTime() !== boundaries.endOfWeek.getTime(),
        cards: this.cardsInRange(boundaries.startOfRemainingWeek, boundaries.endOfWeek)
      });
    }

    sections.push({
      key: ScheduleSectionKey.NextWeek,
      label: "Next Week",
      dateLabel: formatDateRangeLabel(boundaries.startOfNextWeek, boundaries.endOfNextWeek),
      showCardDates: true,
      cards: this.cardsInRange(boundaries.startOfNextWeek, boundaries.endOfNextWeek)
    });

    return sections;
  });

  lastWeekCards = computed(() => {
    const startOfLastWeek = this.dateUtils.addDays(this.getWeekBoundaries().startOfWeek, -7);

    return this.cardsInRange(startOfLastWeek, this.dateUtils.addDays(startOfLastWeek, 6));
  });

  thisWeekTabCardCount = computed(() =>
    this.overdueCards().length
    + this.scheduleSections().reduce((total, section) => total + section.cards.length, 0));

  /** The filtered-out empty state replaces "Nothing scheduled" only when filters caused the gap. */
  readonly isCurrentTabFilteredEmpty = computed(() => {
    if (this.appliedFilterCount() === 0) {
      return false;
    }

    return this.activeTab() === ScheduleTab.ThisWeek
      ? this.thisWeekTabCardCount() === 0
      : this.lastWeekCards().length === 0;
  });

  async ngOnInit(): Promise<void> {
    this.syncMetadata = this.dataSyncMetadataService.getSyncMetadata() || {};

    this.dataSyncMetadataService.syncMetadataChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(metadata => {
      this.syncMetadata = metadata;
    });

    this.today = new Date();

    // A rejection here must not abort the rest of ngOnInit: the refresh below is also what drives
    // the server-side EDH import, so failing early would stop new jobs ever reaching the device.
    try {
      this.lotPrimaryIdentifier.set(
        (await this.featureService.getEffectiveConfig(AdminConfigKey.LotPrimaryIdentifier)) ?? LotPrimaryIdentifier.LotBlock
      );
    } catch (err) {
      this.logger.error("[CM Schedule] Failed to read the lot primary identifier config:", err);
    }

    this.dataSyncMetadataService.bootstrapSyncMetadata();

    // Render immediately from whatever is already cached locally.
    const cachedJobs = await this.constructionManagerScheduleService.getConstructionManagerScheduleFromCache();
    const hadCachedData = cachedJobs.length > 0;

    // A bad cached record must not block the refresh below: that request is also what drives the
    // server-side EDH import, so failing here would stop new jobs ever reaching the device.
    if (hadCachedData) {
      try {
        await this.applySchedule(cachedJobs);
      } catch (err) {
        this.logger.error("[CM Schedule] Failed to render the cached schedule:", err);
      }
    }

    this.isRefreshingInBackground.set(true);

    // getConstructionManagerSchedule() swallows network/sync errors internally and always resolves with
    // whatever's in IndexedDB, so a resolved promise alone doesn't mean anything actually changed. Only
    // scheduleRefreshed firing (emitted strictly before the resolved array is read) means a real sync happened.
    let refreshSucceeded = false;
    const refreshSucceededSub = this.constructionManagerScheduleService.scheduleRefreshed.subscribe(() => {
      refreshSucceeded = true;
    });

    const backgroundRefresh = firstValueFrom(
      this.constructionManagerScheduleService.getConstructionManagerSchedule()
    ).then(async freshJobs => {
      await this.applySchedule(freshJobs);
      this.isRefreshingInBackground.set(false);
      if (hadCachedData && refreshSucceeded) {
        this.dataUpdatedPulse.set(true);
      }
    })
      .catch(err => {
        this.logger.error("[CM Schedule] Failed to refresh schedule from server:", err);
        this.isRefreshingInBackground.set(false);
      })
      .finally(() => {
        refreshSucceededSub.unsubscribe();
      });

    // With no cached data to show yet, fall back to the original behavior of
    // waiting on the network fetch before rendering (full skeleton stays up).
    if (!hadCachedData) {
      await backgroundRefresh;
    }
  }

  private async applySchedule(jobs: InspectionJob[]): Promise<void> {
    const lots = await this.lotService.getLotsByIds(jobs.map(j => j.lotId));
    const lotsById = lots.reduce((prev, current) => {
      prev[current.id] = current;
      return prev;
    }, {} as { [key: number]: Lot });

    const bq9Enabled = await firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ9));

    const bq6Enabled = await firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ6));

    const bq234Enabled = await firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ234));

    const bq15Enabled = await firstValueFrom(this.featureService.isEnabled(FeatureFlag.EnableBQ15));

    // A job can be cached locally before the server materializes its checklist structure.
    // Track which lots actually have that structure on device so BQ8 cannot be started without it.
    const lotIdsWithChecklist = new Set<number>();
    await Promise.all(lots.map(async lot => {
      if (await this.checklistService.hasChecklist(lot.id)) {
        lotIdsWithChecklist.add(lot.id);
      }
    }));

    const inspectionJobs = await Promise.all(jobs.map(async job => {
      const lot = lotsById[job.lotId];
      const hasStarted = await this.hasJobStarted(job);
      const detail = { ...lot, ...job, zones: [] } as InspectionJobDetail;
      const isBq8Complete = jobs.find(j => j.lotId === job.lotId && j.buildQualityStep === 8)?.isComplete || false;

      return createScheduleJobDetail(detail, {
        hasStarted,
        bq15Enabled,
        bq234Enabled,
        bq6Enabled,
        bq9Enabled,
        isBq8Complete,
        isChecklistMaterialized: lotIdsWithChecklist.has(job.lotId)
      });
    }));

    this.lotCards.set(buildScheduleLotCards(inspectionJobs, this.createLotCardContext()));

    this.hydrateReadyJobImages(inspectionJobs);

    this.dataLoaded = true;
  }

  private createLotCardContext(): ScheduleLotCardContext {
    const today = this.dateUtils.normalizeToLocalDate(new Date()).getTime();

    return {
      isWithinReadyWindow: job => this.businessDaysUntil(job.scheduledDate) <= READY_WINDOW_BUSINESS_DAYS,
      isOverdue: job => !job.isComplete && this.businessDaysUntil(job.scheduledDate) < 0,
      // Null means nothing is stored locally, so there is no upload outstanding.
      hasPendingUpload: job => {
        const uploadState = this.syncStatus(job);

        return uploadState !== null && uploadState !== JobStatus.CompleteUploaded;
      },
      wasCompletedToday: job => !!job.submittedAt
        && this.dateUtils.normalizeToLocalDate(job.submittedAt).getTime() === today
    };
  }

  private businessDaysUntil(date: Date): number {
    return this.dateUtils.businessDaysDiff(
      this.dateUtils.normalizeToLocalDate(new Date()),
      this.dateUtils.normalizeToLocalDate(date)
    );
  }

  private hydrateReadyJobImages(jobs: ScheduleJobDetail[]): void {
    const jobIds = jobs
      .filter(job => this.businessDaysUntil(job.scheduledDate) <= READY_WINDOW_BUSINESS_DAYS)
      .sort((left, right) => {
        const isLeftJobVisibleOnScreen = this.isJobVisibleOnScreen(left);
        const isRightJobVisibleOnScreen = this.isJobVisibleOnScreen(right);

        if (isLeftJobVisibleOnScreen === isRightJobVisibleOnScreen) {
          return 0;
        }

        return isLeftJobVisibleOnScreen ? -1 : 1;
      })
      .map(j => j.id);

    if (jobIds.length === 0) {
      return;
    }

    this.mediaHydration.hydrateByJobIds(jobIds, true)
      .catch(err => {
        this.logger.error("[CM Schedule] Hydration failed:", err);
      });
  }

  private isJobVisibleOnScreen(job: ScheduleJobDetail): boolean {
    const date = job.getEffectiveFilterDate();

    return this.dateUtils.isDateThisWeek(date) || this.dateUtils.isDateLastWeek(date);
  }

  private getWeekBoundaries(): ScheduleWeekBoundaries {
    const today = this.dateUtils.normalizeToLocalDate(new Date());
    const endOfWeek = this.dateUtils.endOfWeek(today);
    const startOfNextWeek = this.dateUtils.addDays(endOfWeek, 1);

    return {
      today,
      startOfWeek: this.dateUtils.startOfWeek(today),
      endOfWeek,
      startOfRemainingWeek: this.dateUtils.addDays(today, 1),
      startOfNextWeek,
      endOfNextWeek: this.dateUtils.addDays(startOfNextWeek, 6)
    };
  }

  private cardsInRange(from: Date, to: Date): ScheduleLotCard[] {
    return this.visibleCards().filter(card => {
      const cardDate = this.dateUtils.normalizeToLocalDate(card.effectiveDate).getTime();

      return cardDate >= from.getTime() && cardDate <= to.getTime();
    });
  }

  status(job: AnyJob): JobStatus {
    if (job.isComplete) {
      return this.getUploadState(job);
    }

    const diff = this.dateUtils.businessDaysDiff(
      this.dateUtils.normalizeToLocalDate(new Date()),
      this.dateUtils.normalizeToLocalDate(job.scheduledDate)
    );

    if (diff < 0) {
      return JobStatus.Overdue;
    }

    if (diff <= READY_WINDOW_BUSINESS_DAYS) {
      return JobStatus.Ready;
    }

    return JobStatus.Upcoming;
  }

  /**
   * Upload state of whatever the job has stored on this device. Independent of whether the step is
   * finished, since an in-progress step can still have photos and notes waiting to sync.
   * Null when nothing is stored locally, so there is no upload to report.
   */
  syncStatus(job: AnyJob): JobStatus | null {
    return this.getSyncMetadataEntries(job).length > 0 ? this.getUploadState(job) : null;
  }

  private getSyncMetadataEntries(job: AnyJob): SyncMetadata[] {
    return Object.values(this.syncMetadata[job.id] ?? {})
      .filter(entry => typeof entry === "object") as SyncMetadata[];
  }

  private getUploadState(job: AnyJob): JobStatus {
    const entries = this.getSyncMetadataEntries(job);
    const isUploadComplete = entries.length > 0 && entries.every(entry => {
      const { localTotalCount, remoteCount } = this.dataSyncMetadataService.getEffectiveSyncMetadata(entry);
      return remoteCount >= localTotalCount;
    });

    if (isUploadComplete) {
      return JobStatus.CompleteUploaded;
    }

    if (this.isOnline === false) {
      return JobStatus.CompleteOffline;
    }

    if (this.syncStatusService.getSyncStatus() === SyncStatus.Incomplete) {
      return JobStatus.CompletePending;
    }

    return JobStatus.CompleteInProgress;
  }

  viewJobSyncStatus(item: InspectionJob): void {
    this.selectedJob = item;
    const selectedJobMetadata = this.syncMetadata[item.id];
    if (selectedJobMetadata) {
      this.isBottomSheetOpen = true;
    } else {
      this.toast.warn("No sync information available for this job yet. Please try again in a few moments.");
      this.dataSyncMetadataService.updateSyncMetadata();
    }
  }

  onSyncSheetClose(): void {
    this.isBottomSheetOpen = false;
    this.selectedJob = null;
  }

  getOverdueJobRoute(job: InspectionJob): string {
    return `/cm/bq${job.buildQualityStep}/job/${job.id}`;
  }

  trackByLotId(_idx: number, card: ScheduleLotCard) {
    return card.lotId;
  }

  /** Bound as a field so the card keeps a stable reference across change detection. */
  readonly syncStatusOf = (job: AnyJob): JobStatus | null => this.syncStatus(job);

  setActiveTab(tab: ScheduleTab): void {
    this.activeTab.set(tab);
  }

  openFilterSheet(): void {
    this.isFilterSheetOpen = true;
  }

  removeFilterChip(chip: ScheduleFilterChip): void {
    this.scheduleFilterService.removeChip(chip.filterId, chip.value);
  }

  onChipRowWheel(event: WheelEvent): void {
    const target = event.currentTarget as HTMLElement;
    if (target) {
      const hasHorizontalOverflow = target.scrollWidth > target.clientWidth;
      if (hasHorizontalOverflow) {
        event.preventDefault();
        target.scrollLeft += event.deltaY;
      }
    }
  }

  viewLotSummary(card: ScheduleLotCard): void {
    this.router.navigate(["cm", "all-my-lots", "community", card.communityId, "lot", card.lotId, "summary"], {
      state: { fromAllLots: false }
    });
  }

  private async hasJobStarted(job: InspectionJob): Promise<boolean> {
    if (job.isComplete || !!job.startedAt) {
      return true;
    }

    if (!canUseIssueHistoryForStartedState(job)) {
      return false;
    }

    return await this.jobIssueService.hasAnyIssueForJob(job.id);
  }

}